import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getBlogPostBySlug, type BlogPost } from '../api/blogPosts';
import { getBlogTitle, getBlogContent } from '../utils/blogUtils';
import { Container } from '@mui/material';

const BlogPostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [blogPost, setBlogPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchBlogPost = async () => {
      if (!slug) return;

      try {
        setLoading(true);
        const post = await getBlogPostBySlug(slug);
        setBlogPost(post);
      } catch (err) {
        console.error('Error fetching blog post:', err);
        setError(t('blog.failedToLoad'));
      } finally {
        setLoading(false);
      }
    };

    fetchBlogPost();
  }, [slug, t]);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-3/4 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-6"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            <div className="h-4 bg-gray-200 rounded w-4/6"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !blogPost) {
    return (
      <Container maxWidth="lg" sx={{ margin: '0 auto', p: { xs: 2, md: 3 } }}>
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            {error || t('blog.pageNotFound')}
          </h1>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {t('blog.goHome')}
          </button>
        </div>
      </div>
      </Container>
    );
  }

  // Get the correct language content
  const currentLanguage = i18n.language;
  const title = getBlogTitle(blogPost, currentLanguage);
  const content = getBlogContent(blogPost, currentLanguage);

  return (
    <Container maxWidth="lg" sx={{ margin: '0 auto', p: { xs: 2, md: 3 } }}>
      <article className="w-full bg-white rounded-lg shadow-sm p-8">
        {/* Featured Image */}
        {blogPost.featuredImageUrl && (
          <div className="mb-8">
            <img
              src={blogPost.featuredImageUrl}
              alt={title}
              className="w-full h-64 object-cover rounded-lg"
            />
          </div>
        )}

        {/* Title */}
        <h1 className="text-3xl font-bold text-gray-900 mb-4">
          {title}
        </h1>

        {/* Meta Information */}
        <div className="flex items-center text-sm text-gray-500 mb-6 space-x-4">
          <span>
            {t('blog.published')}: {new Date(blogPost.publishedAt).toLocaleDateString()}
          </span>
          {blogPost.tags.length > 0 && (
            <div className="flex space-x-2">
              {blogPost.tags.map((tag, index) => (
                <span
                  key={index}
                  className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Content */}
        <div
          className="blog-content text-gray-700 leading-relaxed"
          dangerouslySetInnerHTML={{ __html: content }}
        />
      </article>
    </Container>
  );
};

export default BlogPostPage;
